import React from 'react';
import { Col, Container, Row } from 'reactstrap';

import { Tenants } from 'store/tenants';
import {
  IPayout,
  PayoutWithRestaurantAndCompany,
} from 'types';
import CollapsibleCard from '../../common/CollapsibleCard';
import { currency } from 'globals/currency';
import SimpleTableWithSorting, {
  generateColumn,
  getRTOriginal,
  TableCellProps,
} from '../../common/SimpleTableWithSorting';
import { formatDate, toDateInputValue } from 'utils/date-time';
import MarkPayoutAsPaid from './MarkPayoutAsPaid';
import ClickToCopy from '../../common/ClickToCopy';
import { toast } from 'react-toastify';
import { fixDecimal } from 'utils/general';
import { OtoToggle } from '../../common';

export type GroupedPayouts = Record<Tenants, TenantPayouts>;

type TenantPayouts = Record<number | string, RestaurantPayouts>;

export type RestaurantPayouts = {
  payouts: PayoutWithRestaurantAndCompany[];
  sum_to_payout: number;
};

export type CompanyPayoutsGroup = {
  restaurantsData: RestaurantPayouts[];
  sum_to_payout: number;
};

const columns = [
  generateColumn('id', {
    customHeader: '# wypłaty',
    customCellFormatter: (
      props: TableCellProps<IPayout>
    ) => <MarkPayoutAsPaid payout={getRTOriginal(props)} />,
  }),
  generateColumn('restaurant.company.name', {
    customHeader: 'Firma',
  }),
  generateColumn('restaurant.name', {
    customHeader: 'Restauracja',
  }),
  generateColumn('period_from', {
    customHeader: 'Okres od',
    customCellFormatter: (props) => toDateInputValue(new Date(props.getValue())),
  }),
  generateColumn('period_to', {
    customHeader: 'Okres do',
    customCellFormatter: (props) => toDateInputValue(new Date(props.getValue())),
  }),
  generateColumn('sum_to_payout', { customHeader: 'Kwota do wypłaty' }),
  generateColumn('paid_at', { customHeader: 'Data wypłacenia' }),
];

const getFlattenedTenantPayouts = (
  tenantPayouts: RestaurantPayouts[]
): IPayout[] => {
  return tenantPayouts.map((restaurantData) => restaurantData.payouts).flat();
};

export const groupPayoutsByCompany = (
  tenantPayouts: RestaurantPayouts[]
): CompanyPayoutsGroup[] => {
  const groups: CompanyPayoutsGroup[] = [];
  const groupsByCompanyId = new Map<number, CompanyPayoutsGroup>();

  tenantPayouts.forEach((restaurantData) => {
    const companyId = restaurantData.payouts[0]?.restaurant?.company?.id;

    if (!companyId) {
      groups.push({
        restaurantsData: [restaurantData],
        sum_to_payout: restaurantData.sum_to_payout,
      });
      return;
    }

    const existingGroup = groupsByCompanyId.get(companyId);
    if (existingGroup) {
      existingGroup.restaurantsData.push(restaurantData);
      existingGroup.sum_to_payout = fixDecimal(
        existingGroup.sum_to_payout + restaurantData.sum_to_payout
      );
    } else {
      const group: CompanyPayoutsGroup = {
        restaurantsData: [restaurantData],
        sum_to_payout: restaurantData.sum_to_payout,
      };
      groupsByCompanyId.set(companyId, group);
      groups.push(group);
    }
  });

  return groups;
};

interface IProps {
  activeTenant: Tenants;
  groupedPayouts: GroupedPayouts;
}

const AdminPayouts: React.FC<IProps> = (props) => {
  const { activeTenant, groupedPayouts } = props;

  const [showZeroPayouts, setShowZeroPayouts] = React.useState(false);
  const [showPaidPayouts, setShowPaidPayouts] = React.useState(false);
  const [groupByCompany, setGroupByCompany] = React.useState(true);

  return (
    <Container className="d-flex flex-column">
      <Row className="mx-0">
        <OtoToggle
          id="show-zero-payouts"
          label="Pokaż zerowe wypłaty"
          checked={showZeroPayouts}
          onChange={() => setShowZeroPayouts(!showZeroPayouts)}
        />
        <OtoToggle
          id="show-paid-payouts"
          label="Pokażuj już opłacone/potrącone"
          checked={showPaidPayouts}
          onChange={() => setShowPaidPayouts(!showPaidPayouts)}
        />
        <OtoToggle
          id="group-payouts-by-company"
          label="Grupuj wypłaty wg. firmy"
          checked={groupByCompany}
          onChange={() => setGroupByCompany(!groupByCompany)}
        />
      </Row>
      {Object.entries(groupedPayouts as GroupedPayouts).map(
        ([tenant, tenantData]) => {
          let tenantPayouts: RestaurantPayouts[] = Object.values(
            tenantData as TenantPayouts
          );

          if (!showZeroPayouts) {
            tenantPayouts = tenantPayouts
              .filter((item) => item.sum_to_payout !== 0)
              .map((item) => ({
                ...item,
                payouts: item.payouts.filter(
                  (payout) => payout.sum_to_payout !== 0
                ),
              }));
          }

          if (!showPaidPayouts) {
            tenantPayouts = tenantPayouts.filter(
              (item) => !item.payouts.some((payout) => payout.paid_at)
            );
          }

          tenantPayouts = tenantPayouts.sort(
            (a, b) => b.sum_to_payout - a.sum_to_payout
          );

          const flattenedTenantPayouts =
            getFlattenedTenantPayouts(tenantPayouts);

          if (!activeTenant || tenant !== activeTenant) {
            return null;
          }

          const payoutGroups: CompanyPayoutsGroup[] = (
            groupByCompany
              ? groupPayoutsByCompany(tenantPayouts)
              : tenantPayouts.map((restaurantData) => ({
                  restaurantsData: [restaurantData],
                  sum_to_payout: restaurantData.sum_to_payout,
                }))
          ).sort((a, b) => b.sum_to_payout - a.sum_to_payout);

          const possiblePayouts = payoutGroups.filter(
            (group) => group.sum_to_payout > 0
          );

          const sumToPayoutAll = payoutGroups.reduce(
            (acc, item) => acc + item.sum_to_payout,
            0
          );
          const sumToPayoutPossible = possiblePayouts.reduce(
            (acc, item) => acc + item.sum_to_payout,
            0
          );

          return (
            <>
              <div className="h2">Wypłaty dla {tenant}</div>
              <h4 className="font-weight-bold">
                Możliwe do opłacenia (dodatnie): {possiblePayouts.length} sztuk
                ({fixDecimal(sumToPayoutPossible)} {currency})
              </h4>
              <h5>
                Wszystkie: {payoutGroups.length} sztuk (
                {fixDecimal(sumToPayoutAll)} {currency})
              </h5>

              {payoutGroups?.length > 0 ? (
                <Row className="font-weight-bold bb-1 mb-2">
                  <Col>Firma / restauracja</Col>
                  <Col className="mw-150">Do wypłaty</Col>
                  <Col>Tytuł przelewu</Col>
                </Row>
              ) : (
                <div className="bb-1 mb-2 font-weight-bold">
                  Brak wypłat dla {tenant}
                </div>
              )}
              {payoutGroups.map((group, index) => {
                const restaurant = group.restaurantsData[0].payouts[0].restaurant;
                const companyName = restaurant?.company?.name || 'Restauracja bez firmy';
                const restaurantNames = group.restaurantsData.map(
                  (restaurantData) =>
                    restaurantData.payouts[0].restaurant?.name ||
                    'Brak restauracji'
                );
                const isLastRow = index === payoutGroups.length - 1;
                const transactionTitle = formatCompanyTransactionTitle(
                  group.restaurantsData
                );
                const periodsCount = getDistinctPeriodsCount(
                  group.restaurantsData
                );

                return (
                  <>
                    <Row
                      className={`bb-1 pt-2 ${
                        group.sum_to_payout < 0 ? 'bg-dimmed' : ''
                      }`}
                    >
                      <Col>
                        <div>{companyName}</div>
                        {restaurantNames.map((restaurantName) => (
                          <div key={restaurantName}>{restaurantName}</div>
                        ))}
                      </Col>
                      <Col className="mw-150">
                        <ClickToCopy
                          onSuccess={() =>
                            toast.info(
                              `Skopiowano kwotę "${group.sum_to_payout}" do schowka`
                            )
                          }
                          textToCopy={group.sum_to_payout.toString()}
                        >
                          {group.sum_to_payout}
                        </ClickToCopy>
                        <div>
                          ({periodsCount}{' '}
                          {periodsCount > 1 ? 'okresy' : 'okres'}
                          {group.restaurantsData.length > 1
                            ? `, ${group.restaurantsData.length} restauracje`
                            : ''}
                          )
                        </div>
                      </Col>
                      <Col>
                        <ClickToCopy
                          onSuccess={() =>
                            toast.info(
                              `Skopiowano tekst "${transactionTitle}" do schowka`
                            )
                          }
                          textToCopy={transactionTitle}
                        >
                          {transactionTitle}
                        </ClickToCopy>
                      </Col>
                    </Row>
                    {isLastRow && (
                      <Row className="bb-1">
                        <Col className="px-0 mt-3">
                          <CollapsibleCard
                            buttonClassName="p-1 mb-0"
                            title={
                              'Szczegóły (tabela wypłat bez grupowania po restaruacji)'
                            }
                          >
                            <SimpleTableWithSorting
                              columns={columns}
                              data={flattenedTenantPayouts}
                              hasFilters
                              pageSize={50}
                              showFooter={false}
                            />
                          </CollapsibleCard>
                        </Col>
                      </Row>
                    )}
                  </>
                );
              })}
            </>
          );
        }
      )}
    </Container>
  );
};

const getPeriodString = (
  payout: PayoutWithRestaurantAndCompany,
  options: { includeSum: boolean; includeSpace?: boolean }
) => {
  const { includeSum, includeSpace = true } = options;

  const from = new Date(payout.period_from);
  const to = new Date(payout.period_to);

  const fromLabel = formatDate(from, 'dd.MM');
  const toLabel = formatDate(to, 'dd.MM');

  const datePart = includeSpace
    ? `${fromLabel} - ${toLabel}`
    : `${fromLabel}-${toLabel}`;

  return `${datePart}${
    includeSum ? ` (${payout.sum_to_payout} ${currency})` : ''
  }`;
};

const formatPeriods = (payouts: PayoutWithRestaurantAndCompany[]): string => {
  if (payouts.length === 1) {
    return getPeriodString(payouts[0], { includeSum: false });
  }
  if (payouts.length === 2) {
    return payouts
      .map((payout) =>
        getPeriodString(payout, {
          includeSum: payouts.length > 1,
        })
      )
      .join(', ');
  }
  const grouped = payouts.reduce(
    (acc, payout, index) => {
      const sep = index === 0 ? '' : ', ';
      return {
        periodsPart: `${acc.periodsPart}${sep}${getPeriodString(payout, {
          includeSum: false,
          includeSpace: false,
        })}`,
        payoutSumPart: `${acc.payoutSumPart}${sep}${payout.sum_to_payout}`,
      };
    },
    {
      periodsPart: '',
      payoutSumPart: '',
    }
  );
  return `${grouped.periodsPart} (${grouped.payoutSumPart} PLN)`;
};

export const formatTransactionTitle = (
  restaurantData: RestaurantPayouts
): string => {
  const isSinglePeriod = restaurantData.payouts.length === 1;

  const periodsPlural = isSinglePeriod ? 'okres' : 'okresy';

  const prefix = `Zwrot kosztów zamówień za ${periodsPlural}`;
  const suffix = 'po potrąceniu prowizji';

  const periodsString = formatPeriods(restaurantData.payouts);

  return `${prefix} ${periodsString} ${suffix}`;
};

const getPeriodKey = (payout: PayoutWithRestaurantAndCompany): string =>
  `${payout.period_from}__${payout.period_to}`;

export const getDistinctPeriodsCount = (
  restaurantsData: RestaurantPayouts[]
): number => {
  const periods = new Set(
    restaurantsData.flatMap((restaurantData) =>
      restaurantData.payouts.map(getPeriodKey)
    )
  );
  return periods.size;
};

export const formatCompanyTransactionTitle = (
  restaurantsData: RestaurantPayouts[]
): string => {
  if (restaurantsData.length === 1) {
    return formatTransactionTitle(restaurantsData[0]);
  }

  type PeriodGroup = {
    payout: PayoutWithRestaurantAndCompany;
    sums: number[];
  };
  const periodGroups = new Map<string, PeriodGroup>();

  restaurantsData.forEach((restaurantData) => {
    restaurantData.payouts.forEach((payout) => {
      const key = getPeriodKey(payout);
      const existing = periodGroups.get(key);
      if (existing) {
        existing.sums.push(payout.sum_to_payout);
      } else {
        periodGroups.set(key, { payout, sums: [payout.sum_to_payout] });
      }
    });
  });

  const sortedPeriods = [...periodGroups.values()].sort(
    (a, b) =>
      new Date(a.payout.period_from).getTime() -
      new Date(b.payout.period_from).getTime()
  );

  const periodsPlural = sortedPeriods.length === 1 ? 'okres' : 'okresy';

  const periodsString = sortedPeriods
    .map(
      ({ payout, sums }) =>
        `${getPeriodString(payout, { includeSum: false })} (${sums.join(
          ', '
        )} PLN)`
    )
    .join(', ');

  return `Zwrot kosztów zamówień za ${periodsPlural} ${periodsString} po potrąceniu prowizji`;
};

export default AdminPayouts;
